"use client";

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  AreaChartComponent,
  BarChartComponent,
  LineChartComponent,
  PieChartComponent,
  RadarChartComponent,
  RadialBarChartComponent,
  TreeMapChartComponent,
  BarChartWithBrushComponent,
  ScatterChartComponent,
  AdvancedScatterChartComponent,
  BubblePlotComponent,
  HeatmapChartComponent,
  FunnelChartComponent,
  BoxplotChartComponent,
  ChartData,
  ChartSuggestion,
} from "../lib/tools/chart-tools";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { TrendingUp, BarChart2, PieChart, LineChart, Activity, TrendingDown, Grid, ScatterChart, ChevronRight } from "lucide-react";

// A card component to display chart suggestions
const ChartSuggestionCard = ({ suggestion }: { suggestion: ChartSuggestion }) => {
  const handleClick = () => {
    console.log("Suggestion clicked:", suggestion.query);
    // Dispatch a custom event that will be caught by the parent
    const event = new CustomEvent('chart-suggestion-click', {
      bubbles: true,
      detail: { query: suggestion.query }
    });
    document.dispatchEvent(event);
  };
  
  // Get icon based on chart type
  const getIcon = () => {
    switch (suggestion.chartType) {
      case "bar":
      case "barBrush":
        return <BarChart2 className="h-4 w-4" />;
      case "line":
        return <LineChart className="h-4 w-4" />;
      case "area":
        return <Activity className="h-4 w-4" />;
      case "pie":
        return <PieChart className="h-4 w-4" />;
      case "treemap":
        return <Grid className="h-4 w-4" />;
      case "scatter":
        return <ScatterChart className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <Card 
        className="flex flex-col h-full cursor-pointer hover:border-primary/50 transition-colors"
        onClick={handleClick}
      >
        <CardHeader className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getIcon()}
              <CardTitle className="text-sm font-medium">
                {suggestion.title || `${suggestion.chartType.charAt(0).toUpperCase() + suggestion.chartType.slice(1)} Chart`}
              </CardTitle>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent className="px-4 py-2 text-xs text-muted-foreground">
          {suggestion.reason}
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface AIChartRendererProps {
  chartData: ChartData;
}

export default function AIChartRenderer({ chartData }: AIChartRendererProps) {
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<string>(chartData?.chartType || "");
  const [data, setData] = useState<any[]>(chartData?.data || []);
  const [config, setConfig] = useState<any>(chartData?.config || {});
  const [suggestions, setSuggestions] = useState<ChartSuggestion[]>([]);
  
  // Handler for chart suggestion clicks
  const handleSuggestionClick = useCallback((event: CustomEvent) => {
    if (event.detail?.query) {
      console.log("Chart suggestion clicked:", event.detail.query);
      
      // Send suggestion to parent via a custom event
      const suggestionEvent = new CustomEvent('ai-chart-suggestion', {
        bubbles: true,
        detail: { query: event.detail.query }
      });
      document.dispatchEvent(suggestionEvent);
    }
  }, []);
  
  // Add and remove event listener for chart suggestions
  useEffect(() => {
    // Cast to any to work with CustomEvent
    window.addEventListener('chart-suggestion-click', handleSuggestionClick as any);
    
    return () => {
      window.removeEventListener('chart-suggestion-click', handleSuggestionClick as any);
    };
  }, [handleSuggestionClick]);
  
  // Process and validate chart data on mount
  useEffect(() => {
    try {
      console.log("CHART RENDERER: Initializing with chart data", chartData);
      
      if (!chartData) {
        setError("No chart data provided");
        return;
      }
      
      const { chartType, data, config } = chartData;
      
      if (!chartType) {
        setError("Chart type is missing");
        return;
      }
      
      if (!data || !Array.isArray(data) || data.length === 0) {
        setError("Chart data is missing or empty");
        return;
      }
      
      setChartType(chartType);
      setData(data);
      setConfig(config || {});
      
      // Set chart suggestions if available AND if suggestions are enabled
      // Check the show_suggestions flag from several possible locations
      // Could be in chartData itself, an args property, or in config
      const suggestionsEnabled = 
        (chartData as any).show_suggestions === true || 
        (chartData as any).args?.show_suggestions === true ||
        config?.show_suggestions === true;
      
      console.log("CHART RENDERER: Suggestions enabled flag:", suggestionsEnabled);
      console.log("CHART RENDERER: Config suggestions:", config?.suggestions);
      
      if (suggestionsEnabled && config?.suggestions && Array.isArray(config.suggestions)) {
        console.log("CHART RENDERER: Setting suggestions", config.suggestions);
        setSuggestions(config.suggestions);
      } else {
        // Clear suggestions if disabled
        console.log("CHART RENDERER: Suggestions disabled or not available, clearing");
        setSuggestions([]);
      }
      
      setError(null);
      
      console.log("CHART RENDERER: Rendering chart of type", chartType);
      console.log("CHART RENDERER: With data length", data.length);
    } catch (err) {
      console.error("CHART RENDERER: Error initializing chart", err);
      setError("Failed to initialize chart data");
    }
  }, [chartData]);

  // Get chart icon based on type
  const getChartIcon = () => {
    switch (chartType) {
      case "bar":
      case "barBrush":
        return <BarChart2 className="h-5 w-5 text-primary/80" />;
      case "line":
        return <LineChart className="h-5 w-5 text-primary/80" />;
      case "area":
        return <Activity className="h-5 w-5 text-primary/80" />;
      case "pie":
        return <PieChart className="h-5 w-5 text-primary/80" />;
      case "treemap":
        return <Grid className="h-5 w-5 text-primary/80" />;
      case "scatter":
      case "advancedScatter":
      case "bubblePlot":
        return <ScatterChart className="h-5 w-5 text-primary/80" />;
      default:
        return <Activity className="h-5 w-5 text-primary/80" />;
    }
  };

  // Function to get a date range string for better trend context
  const getDateRange = () => {
    if (!data || data.length < 2) return null;
    
    // Look for date fields in the data
    const firstItem = data[0];
    const lastItem = data[data.length - 1];
    
    const possibleDateFields = ['date', 'month', 'year', 'period', 'time', 'quarter', 'week', 'day'];
    const dateField = Object.keys(firstItem).find(key => 
      possibleDateFields.includes(key.toLowerCase()) || 
      (typeof firstItem[key] === 'string' && 
        (firstItem[key].match(/^\d{4}-\d{2}(-\d{2})?$/) || // YYYY-MM or YYYY-MM-DD
         firstItem[key].match(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i))) // Month names
    );
    
    if (dateField) {
      const firstDate = firstItem[dateField];
      const lastDate = lastItem[dateField];
      return `from ${firstDate} to ${lastDate}`;
    }
    
    return null;
  };

  // Extract human-readable display range for chart context
  const displayRange = useMemo(() => {
    // Try to find a date range first
    const dateRange = getDateRange();
    if (dateRange) {
      return `data ${dateRange}`;
    }
    
    // If we have a descriptive field, use that
    if (config.description) {
      return config.description;
    }
    
    // Try to determine what the chart is showing based on yAxisKeys
    if (config.yAxisKeys && config.yAxisKeys.length > 0) {
      const metrics = config.yAxisKeys.join(", ");
      return `${metrics} data`;
    }
    
    // Default fallback
    return "chart data";
  }, [config, data]);

  // Determine chart component to use based on chartType and variant
  const renderChart = () => {
    if (error) {
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-md">
          {error}
        </div>
      );
    }
    
    if (!data || data.length === 0) {
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-md">
          No data available for chart
        </div>
      );
    }
    
    try {
      const chartVariant = config.variant || "default";
      
      // Common tooltip styling for all charts
      const tooltipStyle = {
        contentStyle: {
          backgroundColor: "hsl(var(--background))",
          borderColor: "hsl(var(--border))",
          borderRadius: "8px",
          padding: "8px 12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          fontSize: "12px",
        },
        itemStyle: { 
          padding: "2px 0",
          color: "hsl(var(--foreground))"
        },
        labelStyle: { 
          marginBottom: "4px", 
          fontWeight: "500",
          color: "hsl(var(--foreground))" 
        },
        wrapperStyle: { 
          zIndex: 1000
        },
        formatter: (value: any, name: string | number, props: any) => {
          // Format the value for display
          let displayValue = value;
          if (typeof value === 'number' && Math.abs(value) >= 1000) {
            displayValue = value.toLocaleString();
          }
          return [displayValue, name];
        },
        // Custom tooltip content with colored indicators
        content: ({ active, payload, label }: any) => {
          if (!active || !payload || !payload.length) return null;
          
          return (
            <div className="custom-tooltip p-2 bg-background border border-border rounded-lg shadow-md max-w-xs">
              {label && <p className="text-xs font-medium mb-1.5">{label}</p>}
              <div className="space-y-1.5">
                {payload.map((entry: any, index: number) => (
                  <div key={index} className="flex items-center gap-1.5 text-[11px]">
                    <div 
                      className="h-2 w-2 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-muted-foreground whitespace-nowrap">{entry.name}:</span>
                    <span className="font-medium">
                      {typeof entry.value === 'number' && Math.abs(entry.value) >= 1000 
                        ? entry.value.toLocaleString() 
                        : entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
      };
      
      switch (chartType) {
        case "bar":
          return <BarChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "line":
          return <LineChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "area":
          return <AreaChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "pie":
          return <PieChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "radar":
          return <RadarChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "radialBar":
          return <RadialBarChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "treemap":
          return <TreeMapChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "scatter":
          return <ScatterChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "barBrush":
          return <BarChartWithBrushComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "advancedScatter":
          return <AdvancedScatterChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "bubblePlot":
          return <BubblePlotComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "heatmap":
          return <HeatmapChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "funnel":
          return <FunnelChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        case "boxplot":
          return <BoxplotChartComponent data={data} config={{ ...config, variant: chartVariant, tooltip: tooltipStyle, show_suggestions: config.show_suggestions }} />;
        default:
          console.error("CHART RENDERER: Unknown chart type:", chartType);
          return (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-md">
              Unknown chart type: {chartType}
            </div>
          );
      }
    } catch (err) {
      console.error("CHART RENDERER: Error rendering chart", err);
      return (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-md">
          Failed to render chart: {err instanceof Error ? err.message : String(err)}
        </div>
      );
    }
  };

  // Get the appropriate chart aspect ratio based on chart type and data size
  const getChartAspectRatio = () => {
    // For treemap charts, use a more square aspect ratio
    if (chartType === "treemap") {
      return data.length > 10 ? "aspect-[1.4/1]" : "aspect-[1.6/1]";
    }
    
    // For bar charts with brush component, use a taller aspect ratio
    if (chartType === "barBrush") {
      // Adjust aspect ratio based on data length
      if (data.length > 20) return "aspect-[1.3/1]";
      if (data.length > 10) return "aspect-[1.5/1]";
      return "aspect-[1.8/1]";
    }
    
    // Default aspect ratio
    return "aspect-[1.9/1]";
  };

 

  // Render the suggestions section if available
  // const renderSuggestions = () => {
  //   if (!suggestions || suggestions.length === 0) {
  //     return null;
  //   }
    
  //   return (
  //     <div className="mt-4">
  //       <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3">
  //         Alternative Chart Suggestions
  //       </h3>
  //       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
  //         {suggestions.map((suggestion, index) => (
  //           <ChartSuggestionCard key={index} suggestion={suggestion} />
  //         ))}
  //       </div>
  //     </div>
  //   );
  // };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <Card className="relative overflow-hidden">
        <CardHeader className="bg-muted/20">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                {getChartIcon()}
                <CardTitle className="text-lg">
                  {config.title || `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`}
                </CardTitle>
              </div>
              {config.description && (
                <CardDescription>{config.description}</CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className={`p-0 sm:p-2 pt-5 ${getChartAspectRatio()}`}>
          {renderChart()}
        </CardContent>
        <CardFooter className="text-xs text-muted-foreground border-t bg-muted/10 gap-2 flex-wrap mt-2 pt-4">
          <span className="inline-flex items-center">
            {chartType.charAt(0).toUpperCase() + chartType.slice(1)} chart showing {displayRange}
          </span>
          {data.length > 0 && (
            <span className="text-muted-foreground/70 opacity-80">• {data.length} data points</span>
          )}
        </CardFooter>
      </Card>
      
      {/* Render suggestions below the chart */}
      {/* {renderSuggestions()} */}
    </motion.div>
  );
} 